import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Loader2 } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1687560466164-1eeddb3b119b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGxpZ2h0JTIwbW9kZXJuJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODE3NjQ1Mzh8MA&ixlib=rb-4.1.0&q=85";

function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex rounded-full border border-slate-300 bg-white/90 overflow-hidden text-xs font-semibold" data-testid="login-lang-switch">
      <button
        type="button"
        data-testid="login-lang-en"
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
      >
        EN
      </button>
      <button
        type="button"
        data-testid="login-lang-zh"
        onClick={() => setLang("zh")}
        className={`px-3 py-1.5 transition-colors ${lang === "zh" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
      >
        中文
      </button>
    </div>
  );
}

export default function Login() {
  const { login, user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg overflow-hidden shadow-sm">
              <img src="/icon-192.png?v=2" alt="Diamond Sharp" className="h-full w-full object-cover" />
            </div>
            <span className="font-heading font-bold text-white text-lg">Diamond Sharp</span>
          </div>
          <LangSwitch />
        </div>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl font-bold text-white leading-tight tracking-tight">
            {t("login.tagline_1")}<br />{t("login.tagline_2")}
          </h2>
          <p className="text-slate-300 mt-4 max-w-sm text-sm leading-relaxed">
            {t("login.desc")}
          </p>
        </div>
        <div className="relative z-10 text-slate-400 text-xs">{t("login.platform")}</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-8 lg:justify-end">
            <div className="lg:hidden flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg overflow-hidden shadow-sm">
                <img src="/icon-192.png?v=2" alt="Diamond Sharp" className="h-full w-full object-cover" />
              </div>
              <span className="font-heading font-bold text-slate-900">Diamond Sharp</span>
            </div>
            <div className="lg:hidden"><LangSwitch /></div>
          </div>

          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{t("login.title")}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-8">{t("login.subtitle")}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-wide uppercase text-slate-500">{t("login.email")}</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition"
                placeholder="you@branch.com"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wide uppercase text-slate-500">{t("login.password")}</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("login.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
